import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, Mail, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { cleaningApi } from '../lib/cleaningApi';

const LOGO_URL = "/pt-logo.png?v=2";

export default function CleaningAdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await cleaningApi.adminLogin(email, password);
      localStorage.setItem('cleaning_admin_token', res.data.token);
      toast.success('Login successful');
      navigate('/cleaning/admin/dashboard');
    } catch (err) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    setForgotLoading(true);
    try {
      await cleaningApi.forgotPassword(forgotEmail);
      toast.success('If this email is registered, a password reset link has been sent.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (err) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="cleaning-forgot-password-page">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Platinum Touch" className="h-16 w-auto mx-auto mb-4" />
            <div className="flex items-center justify-center gap-2 mb-2">
              <Mail className="w-5 h-5 text-emerald-500" />
              <h1 className="text-xl font-bold text-slate-900 font-['Outfit']">Reset Password</h1>
            </div>
            <p className="text-slate-500 text-sm">Enter your email to receive a reset link</p>
          </div>
          <form onSubmit={handleForgotPassword} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
              <input 
                type="email" 
                value={forgotEmail} 
                onChange={e => setForgotEmail(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-emerald-500 outline-none"
                placeholder="admin@example.com" 
                required 
                data-testid="cleaning-forgot-email-input" 
              />
            </div>
            <Button 
              type="submit" 
              disabled={forgotLoading} 
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3" 
              data-testid="cleaning-forgot-submit-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              {forgotLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>
            <button 
              type="button"
              onClick={() => setShowForgotPassword(false)}
              className="w-full flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-emerald-500 transition-colors"
              data-testid="cleaning-back-to-login"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="cleaning-admin-login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Platinum Touch" className="h-16 w-auto mx-auto mb-4" />
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-emerald-500" />
            <h1 className="text-xl font-bold text-slate-900 font-['Outfit']">Car Cleaning Admin</h1>
          </div>
          <p className="text-slate-500 text-sm">Sign in to manage cleaning bookings</p>
        </div>
        <form onSubmit={handleLogin} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-emerald-500 outline-none"
              placeholder="admin@example.com" required data-testid="cleaning-admin-email-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-emerald-500 outline-none"
              placeholder="Enter password" required data-testid="cleaning-admin-password-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3" data-testid="cleaning-admin-login-btn">
            <Lock className="w-4 h-4 mr-2" />
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
          <button 
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className="w-full text-sm text-emerald-500 hover:text-emerald-600 transition-colors font-medium"
            data-testid="cleaning-forgot-password-btn"
          >
            Forgot Password?
          </button>
        </form>
      </div>
    </div>
  );
}
